import { h, Fragment } from 'preact';
import { useMemo } from 'preact/compat';
import type { Candidate, BoxProps } from '../types';
import CandidateBox from './CandidateBox';
import { randomColorList } from '../lib/helpers';

import Spinner from './Spinner';

interface CommonProps {
	candidates: Candidate[];
	CandidateCard?: (props: BoxProps) => h.JSX.Element;
}

type DerivedProps =
	| {
		ajax: true;
		max: number;
		page: number;
		setPage: (page: number) => void;
		postPerPage: number;
		pageRef: {
			current: number;
		}
	} | {
		ajax?: false;
		max?: never;
		page?: never;
		setPage?: never;
		postPerPage?: never;
		pageRef?: never;
	}

type Props = CommonProps & DerivedProps;

/**
 * Index Results.
 * Can display ajax driven results with load more btn,
 * or static results.
 *
 * @param {boolean} props.ajax Determines if results are ajax driven.
 * @param {CommonProps['CandidateCard']} props.CandidateCard Candidate card component.
 */
const Results = ({
	candidates,
	max = 12,
	page = 1,
	setPage = () => { },
	ajax = false,
	CandidateCard = CandidateBox,
	postPerPage = 12,
	pageRef = {
		current: 1,
	},
}: Props) => {
	/**
	 * Only load max results if ajax.
	 */
	let candidatesDisplay = candidates;
	if (ajax) {
		candidatesDisplay = candidates.slice(0, page * postPerPage);
	}

	/**
	 * Random color list.
	 */
	const colorList = useMemo(() => randomColorList(), []);

	return (
		<div className="ea-index-results ea-grid">
			{candidatesDisplay.length > 0 ? (
				<Fragment>
					{candidatesDisplay.map((candidate: Candidate, i: number) => (
						<CandidateCard
							key={candidate.id}
							candidate={candidate}
							color={colorList[i % colorList.length]}
						/>
					))}
					{/**
						 * Load More.
						 * Only display if ajax, max results, and more to display.
						 */
						ajax && max > page && (
							<div className="ea-index-more">
								<button
									type="button"
									className="ea-btn-reset"
									onClick={(e) => {
										e.preventDefault();
										setPage(page + 1);
									}}
									// Request in progress.
									disabled={pageRef.current !== page}
								>
									{/**
									 * Loading spinner if loading.
									 */
										pageRef.current !== page ? (
											<Spinner />
										) : (
											<span>Load More</span>
										)
									}
								</button>
							</div>
						)
					}
				</Fragment>
			) : (
				<div className="ea-no-results">
					<h4>No Results Found</h4>
				</div>
			)}
		</div>
	);
};

export default Results;
